/* Hvattum Studios — product information set and size-guide drawer.

   Progressive enhancement over snippets/hvattum-product-details.liquid, which
   server-renders three native <details> disclosures and, when a guide exists, a
   plain size-guide table. Nothing here creates content; it only changes how the
   content that is already in the document is presented.

   Two upgrades:

     1. From 750px the disclosures become an ARIA tablist. Below 750px they are
        returned to native <details>, which is the better handheld pattern and
        the one that survives with JavaScript switched off. The same nodes serve
        both, so there is no duplicated markup and nothing hidden but focusable.

     2. The size guide is moved into a <dialog> and opened with showModal(),
        which hands focus containment, Escape, the top layer and background
        inertness to the browser rather than reimplementing them.

   Every query is scoped to the custom element instance, so several product
   sections in the theme editor stay independent, and disconnectedCallback
   releases anything global this component took.

   Dawn is not touched: no shared state beyond `body.overflow-hidden`, which is
   the class Dawn's own cart drawer and media modal use, and which is released
   here only if this component was the one that set it. */

/* Reduced motion is not consulted here: the only motion this component has is
   the panel's entrance, which is a CSS transition whose duration token already
   collapses to 0ms under prefers-reduced-motion in hvattum-foundation.css. */
const HVATTUM_DESKTOP_QUERY = '(min-width: 750px)';

class HvattumProductDetails extends HTMLElement {
  constructor() {
    super();

    this.uid = this.dataset.uid || '';
    this.items = Array.from(this.querySelectorAll('[data-item]'));
    this.tablist = this.querySelector('[data-tablist]');
    this.sizeGuide = this.querySelector('[data-sizeguide]');
    this.sizeGuideTrigger = this.querySelector('[data-sizeguide-trigger]');

    this.tabs = [];
    this.activeIndex = 0;
    this.isTabMode = false;

    // null until a mode has actually been applied, so the first run always
    // builds one. Compared against directly rather than inferred from the tab
    // list, which is empty in both the disclosure mode and the initial state.
    this.modeApplied = null;

    this.dialog = null;
    this.lockedScroll = false;
    this.lastFocused = null;

    this.desktopQuery = window.matchMedia(HVATTUM_DESKTOP_QUERY);

    this.onBreakpointChange = this.onBreakpointChange.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onTabClick = this.onTabClick.bind(this);
    this.onTabKeydown = this.onTabKeydown.bind(this);
    this.onSizeGuideTriggerClick = this.onSizeGuideTriggerClick.bind(this);
    this.onDialogClick = this.onDialogClick.bind(this);
    this.onDialogClose = this.onDialogClose.bind(this);
    this.onDialogKeydown = this.onDialogKeydown.bind(this);
  }

  connectedCallback() {
    if (this.items.length > 0) {
      this.applyMode(this.desktopQuery.matches);
      this.desktopQuery.addEventListener('change', this.onBreakpointChange);

      // A media-query change is the right signal, but it is the only signal,
      // and a mode that never switches leaves either dead tab semantics on a
      // phone or unusable disclosures on a desktop. resize is a cheap second
      // route to the same call: applyMode compares against the mode already
      // applied and returns without touching the DOM unless it really changed.
      window.addEventListener('resize', this.onResize);
    }

    if (this.sizeGuide && this.sizeGuideTrigger) {
      this.buildDialog();
    }
  }

  disconnectedCallback() {
    this.desktopQuery.removeEventListener('change', this.onBreakpointChange);
    window.removeEventListener('resize', this.onResize);
    this.releaseScroll();

    if (this.dialog && this.dialog.open) {
      this.dialog.close();
    }
  }

  onBreakpointChange(event) {
    this.applyMode(event.matches);
  }

  onResize() {
    this.applyMode(this.desktopQuery.matches);
  }

  /* ----------------------------------------------------------------------
     Tabs (750px and up) and disclosures (below)
     ---------------------------------------------------------------------- */

  applyMode(isDesktop) {
    if (this.modeApplied === isDesktop) return;

    this.modeApplied = isDesktop;

    if (isDesktop) {
      this.enableTabs();
    } else {
      this.enableDisclosures();
    }
  }

  enableTabs() {
    this.isTabMode = true;

    // Remember which disclosure the visitor had open so the equivalent tab is
    // the one selected, rather than resetting them to the first panel.
    const openIndex = this.items.findIndex((item) => item.open);
    this.activeIndex = openIndex > -1 ? openIndex : 0;

    this.tablist.innerHTML = '';
    this.tablist.setAttribute('role', 'tablist');
    this.tablist.hidden = false;
    this.tabs = [];

    this.items.forEach((item) => {
      const key = item.dataset.key;
      const label = item.querySelector('.hvattum-details__summary-label');
      const panelId = `HvattumPanel-${this.uid}-${key}`;
      const tabId = `HvattumTab-${this.uid}-${key}`;

      const tab = document.createElement('button');
      tab.type = 'button';
      tab.id = tabId;
      tab.className = 'hvattum-details__control hvattum-details__control--tab';
      tab.textContent = label ? label.textContent.trim() : key;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panelId);
      tab.addEventListener('click', this.onTabClick);
      tab.addEventListener('keydown', this.onTabKeydown);

      this.tablist.appendChild(tab);
      this.tabs.push(tab);

      // The <details> element itself becomes the panel. It is forced open so
      // its content is rendered, its summary is taken out of the accessibility
      // tree and the tab list replaces it as the control.
      item.open = true;
      item.id = panelId;
      item.classList.add('hvattum-details__item--tab');
      item.setAttribute('role', 'tabpanel');
      item.setAttribute('aria-labelledby', tabId);
      item.setAttribute('tabindex', '0');

      // The summary is taken out of the page by CSS in tab mode, which removes
      // it from the accessibility tree and the focus order at the same time.
      // No aria-hidden is set: that would flag a focusable element as hidden.
    });

    this.selectTab(this.activeIndex, false);
  }

  enableDisclosures() {
    this.isTabMode = false;

    this.tabs.forEach((tab) => {
      tab.removeEventListener('click', this.onTabClick);
      tab.removeEventListener('keydown', this.onTabKeydown);
    });
    this.tabs = [];

    this.tablist.innerHTML = '';
    this.tablist.removeAttribute('role');
    this.tablist.hidden = true;

    this.items.forEach((item, index) => {
      item.classList.remove('hvattum-details__item--tab');
      item.removeAttribute('role');
      item.removeAttribute('aria-labelledby');
      item.removeAttribute('tabindex');
      item.hidden = false;

      // Carry the selected tab across as the open disclosure.
      item.open = index === this.activeIndex;
    });
  }

  selectTab(index, moveFocus = true) {
    if (index < 0 || index >= this.tabs.length) return;

    this.activeIndex = index;

    this.tabs.forEach((tab, tabIndex) => {
      const isActive = tabIndex === index;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');

      // `hidden` keeps an inactive panel out of the page for everyone: not
      // painted, not read, not reachable by keyboard.
      this.items[tabIndex].hidden = !isActive;
    });

    if (moveFocus) this.tabs[index].focus();
  }

  onTabClick(event) {
    this.selectTab(this.tabs.indexOf(event.currentTarget), false);
  }

  onTabKeydown(event) {
    const lastIndex = this.tabs.length - 1;
    let nextIndex = null;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = this.activeIndex === lastIndex ? 0 : this.activeIndex + 1;
        break;
      case 'ArrowLeft':
        nextIndex = this.activeIndex === 0 ? lastIndex : this.activeIndex - 1;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = lastIndex;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.selectTab(nextIndex);
  }

  /* ----------------------------------------------------------------------
     Size-guide dialog
     ---------------------------------------------------------------------- */

  buildDialog() {
    this.dialog = document.createElement('dialog');
    this.dialog.className = 'hvattum-sizeguide-dialog';
    this.dialog.id = `HvattumSizeGuideDialog-${this.uid}`;
    this.dialog.setAttribute('aria-labelledby', `HvattumSizeGuideTitle-${this.uid}`);

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'hvattum-sizeguide__close';
    close.setAttribute('aria-label', 'Close size guide');
    close.innerHTML =
      '<svg aria-hidden="true" focusable="false" viewBox="0 0 16 16" width="14" height="14">' +
      '<path d="M1 1L15 15M15 1L1 15" stroke="currentColor" stroke-width="1.25" fill="none"/></svg>';
    close.addEventListener('click', () => this.closeDialog());

    // The guide element is moved, not copied, so the table exists exactly once.
    this.sizeGuide.hidden = false;
    this.sizeGuide.querySelector('.hvattum-sizeguide__header').appendChild(close);
    this.dialog.appendChild(this.sizeGuide);
    this.appendChild(this.dialog);

    this.dialog.addEventListener('click', this.onDialogClick);
    this.dialog.addEventListener('close', this.onDialogClose);
    this.dialog.addEventListener('keydown', this.onDialogKeydown);
    this.sizeGuideTrigger.addEventListener('click', this.onSizeGuideTriggerClick);
  }

  onSizeGuideTriggerClick(event) {
    this.openDialog(event.currentTarget);
  }

  openDialog(trigger) {
    if (!this.dialog || this.dialog.open) return;

    // Restore focus to the exact control that opened the dialog.
    this.lastFocused = trigger || document.activeElement;

    this.dialog.showModal();
    this.lockScroll();

    // showModal() focuses the dialog's first focusable child, which is the
    // close button — the intended landing point. Nothing else is needed here:
    // the panel's open position is carried by the `open` attribute in CSS, so
    // there is no class to add and no animation frame to wait for.
  }

  /* Closing is immediate rather than animated out. Deferring the close to a
     transition would mean the dialog stays open — and focus stays trapped —
     for as long as that transition takes to report back, which is exactly the
     thing that cannot be relied on. It also makes the close button behave
     identically to Escape, which the browser closes instantly regardless. */
  closeDialog() {
    if (!this.dialog || !this.dialog.open) return;

    this.dialog.close();
    this.afterClose();
  }

  /* Unlocking the page and handing focus back are too consequential to hang on
     a single event, so they are done here and called from every route out:
     the close button, the backdrop, Escape, and the dialog's own close event as
     a backstop. Guarded at both ends and safe to run twice. */
  afterClose() {
    if (this.dialog.open) return;

    this.releaseScroll();

    if (this.lastFocused && document.contains(this.lastFocused)) {
      this.lastFocused.focus();
    }

    this.lastFocused = null;
  }

  /* Escape is the browser's to handle, and it is not intercepted: close() below
     simply runs first, which leaves the browser's own default action to act on
     an already-closed dialog and do nothing. Nothing is preventDefault-ed, so a
     fault in this component can never leave Escape dead. */
  onDialogKeydown(event) {
    if (event.key === 'Escape') this.closeDialog();
  }

  onDialogClick(event) {
    // A click on the backdrop is reported against the dialog element itself.
    if (event.target === this.dialog) this.closeDialog();
  }

  /* Fires for the close button, Escape, a backdrop click and any programmatic
     close, so scroll and focus are restored in one place whichever route the
     visitor took out. */
  onDialogClose() {
    // The close event is delivered asynchronously, so the panel can already
    // have been reopened by the time it lands. afterClose() checks for exactly
    // that and leaves an open dialog alone.
    this.afterClose();
  }

  /* ----------------------------------------------------------------------
     Scroll locking

     Dawn's cart drawer and product-media modal use the same body class. Only
     the component that added it removes it, so closing this dialog can never
     unlock the page underneath one of Dawn's own surfaces.
     ---------------------------------------------------------------------- */

  lockScroll() {
    // Already ours from a close whose event has not been delivered yet: keep
    // the ownership rather than handing it away, or the pending release would
    // find the flag cleared and leave the page locked for good.
    if (this.lockedScroll) return;

    // Someone else's lock — Dawn's cart drawer or media modal. Leave it alone
    // and take no ownership, so closing this panel cannot unlock the page
    // underneath one of theirs.
    if (document.body.classList.contains('overflow-hidden')) return;

    document.body.classList.add('overflow-hidden');
    this.lockedScroll = true;
  }

  releaseScroll() {
    if (!this.lockedScroll) return;

    document.body.classList.remove('overflow-hidden');
    this.lockedScroll = false;
  }
}

if (!customElements.get('hvattum-product-details')) {
  customElements.define('hvattum-product-details', HvattumProductDetails);
}
